import React, { useEffect, useState } from 'react';
import { MemoryRouter } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { CircularProgress } from '@mui/material';
import { StyledCardSection, StepsRouter } from '@fnol/library/src/components';
import { authClient } from '@fnol/library/src/api/auth';
import { submitTemporaryData } from '../../rpc/actions';
import stepsComposer from '../../lib/stepsComposer';
import { bodyHarm, constValues, legalPersonType } from '../../lib/consts';
import { prepareDataForSubmit } from '../../lib/helpers';
import { getApplicationId, getIsSalesForce } from '@fnol/library/src/lib';
import { IntroMessagesSlider } from '@fnol/library/src/components/IntroMessagesSlider';
import introMessagesStepsComposer from '../../lib/introMessagesStepsComposer';

const MtplApp = () => {
    const [loggedIn, setLoggedIn] = useState<boolean>(false);
    useEffect(() => {
        authClient.login('mtpl', 230).then(() => setLoggedIn(true));
    }, []);

    return (
        <>
            {loggedIn ? (
                <Formik
                    enableReinitialize
                    initialValues={{
                        introMessagesOpen: true,
                        introMessagesSkipSteps: getIsSalesForce(getApplicationId()) ? [1, 2] : [],
                        regNumber: '',
                        street: '',
                        cityInSerbia: '',
                        submitterPhone: constValues.dialCodeSerbia,
                        owner: '',
                        ownerOfVehicle: '',
                        ownerEntity: '',
                        policyOrRegistrationNumber: '', // policy detail screen
                        policyNumber: '', // policy detail screen
                        registrationNumber: '', // policy detail screen
                        dateOfOccurance: null, // policy detail screen
                        hourOfOccurance: '', // policy detail screen
                        minuteOfOccurance: '', // policy detail screen

                        requestType: '', // submitter details screen: type of request. enum requestType and list in requestTypeOptions
                        submitterType: legalPersonType.initialValue, // submitter type from enum legalPersonType
                        submitterIndividualName: '', // individual component for submitter
                        submitterIndividualLastName: '', // individual component for submitter
                        submitterIndividualUCN: '', // individual component for submitter
                        submitterIndividualCountry: constValues.countrySerbiaId, // individual component for submitter
                        submitterIndividualMyCountry: '', // individual component for submitter
                        submitterIndividualCity: '', // individual component for submitter
                        submitterIndividualCityInSerbia: '', // individual component for submitter
                        submitterIndividualStreet: '', // individual component for submitter
                        submitterIndividualPhone: constValues.dialCodeSerbia, // individual component for submitter
                        submitterIndividualEmail: '', // individual component for submitter
                        submitterIndividualShouldShowBodyHarmForm: bodyHarm.InitialValue, // individual component for submitter
                        submitterIndividualReportingImmaterialDamage: null, // individual component for submitter
                        submitterIndividualPersonRoleId: '', // individual component for submitter
                        submitterIndividualImmaterialDamageDescription: '', // individual component for submitter
                        submitterIndustrialClassificationType: '', // legal/leasing compoment for submitter
                        submitterLegalEntityCountry: constValues.countrySerbiaId, // legal/leasing compoment for submitter
                        submitterLegalEntityMyCountry: '', // legal/leasing compoment for submitter
                        submitterLegalEntityCity: '', // legal/leasing compoment for submitter
                        submitterLegalEntityCityInSerbia: '', // legal/leasing compoment for submitter
                        submitterLegalEntityStreet: '', // legal/leasing compoment for submitter
                        submitterLegalEntityPhone: constValues.dialCodeSerbia, // legal/leasing compoment for submitter
                        submitterLegalEntityContactPhone: constValues.dialCodeSerbia, // legal/leasing compoment for submitter
                        submitterLegalEntityName: '', // legal/leasing compoment for submitter
                        submitterLegalEntityID: '', // legal/leasing compoment for submitter
                        submitterLegalEntityVAT: '', // legal/leasing compoment for submitter
                        submitterLegalEntityEmail: '', // legal/leasing compoment for submitter
                        submitterLegalEntityContactName: '', // legal/leasing compoment for submitter
                        submitterLegalEntityContactLastName: '', // legal/leasing compoment for submitter
                        submitterLegalEntityContactEmail: '', // legal/leasing compoment for submitter
                        submitterLeasingEntityID: '', // legal/leasing compoment for submitter
                        submitterLegalNameForDisplay: '', //legal leasing entity component

                        streetClaim: '', // claim details screen
                        city: '', // claim details screen
                        cityInSerbiaClaim: '', // claim details screen
                        country: constValues.countrySerbiaId, // claim details screen
                        myCountry: '', // claim details screen
                        cause: '', // claim details screen
                        vehicleParked: '', // claim details screen
                        euReport: '', // claim details screen
                        reportedToPolice: '', // claim details screen
                        policeOnScene: '', // claim details screen
                        policeInspection: '', // claim details screen
                        alcoholTest: '', // claim details screen
                        howDidDamageOccur: '', // claim details screen

                        damageType: '', // damage screen
                        vehicleType: '', // damage screen
                        chassisNumber: '', // damage screen
                        vehicleBrand: '', // damage screen
                        vehicleModel: '', // damage screen
                        vehicleReg: '', // damage screen
                        vehicleProductionYear: '', // damage screen
                        reportedClaims: [], // damage screen

                        objectType: '', // object damage screen
                        objectDescription: '', // object damage screen
                        objectConstructionYear: '', // object damage screen
                        objectLocation: '', // object damage screen
                        objectDamageDescription: '', // object damage screen
                        moreObjectDamagesDescription: '', // object damage screen

                        ownerType: legalPersonType.initialValue, // owner type from enum legalPersonType
                        ownerIndividualName: '', // individual component for owner
                        ownerIndividualLastName: '', // individual component for owner
                        ownerIndividualUCN: '', // individual component for owner
                        ownerIndividualCountry: constValues.countrySerbiaId, // individual component for owner
                        ownerIndividualMyCountry: '', // individual component for owner: textual representation of selected country
                        ownerIndividualCity: '', // individual component for owner
                        ownerIndividualCityInSerbia: '', // individual component for owner
                        ownerIndividualStreet: '', // individual component for owner
                        ownerIndividualPhone: constValues.dialCodeSerbia, // individual component for owner
                        ownerIndividualEmail: '', // individual component for owner
                        ownerIndividualSubmitterRelationId: '', // individual component for owner
                        ownerIndividualShouldShowBodyHarmForm: bodyHarm.InitialValue, // individual component for owner
                        ownerIndividualReportingImmaterialDamage: null, // individual component for owner
                        ownerIndividualPersonRoleId: '', // individual component for owner
                        ownerIndividualImmaterialDamageDescription: '', // individual component for owner

                        ownerLeasingEntityID: '', // legal/leasing compoment for owner
                        ownerIndustrialClassificationType: '', // legal/leasing compoment for owner
                        ownerLegalEntityCountry: constValues.countrySerbiaId, // legal/leasing compoment for owner
                        ownerLegalEntityMyCountry: '', // legal/leasing compoment for owner
                        ownerLegalEntityCity: '', // legal/leasing compoment for owner
                        ownerLegalEntityCityInSerbia: '', // legal/leasing compoment for owner
                        ownerLegalEntityStreet: '', // legal/leasing compoment for owner
                        ownerLegalEntityPhone: constValues.dialCodeSerbia, // legal/leasing compoment for owner
                        ownerLegalEntityContactPhone: constValues.dialCodeSerbia, // legal/leasing compoment for owner
                        ownerLegalEntityName: '', // legal/leasing compoment for owner
                        ownerLegalEntityID: '', // legal/leasing compoment for owner
                        ownerLegalEntityVAT: '', // legal/leasing compoment for owner
                        ownerLegalEntityEmail: '', // legal/leasing compoment for owner
                        ownerLegalEntityContactName: '', // legal/leasing compoment for owner
                        ownerLegalEntityContactLastName: '', // legal/leasing compoment for owner
                        ownerLegalEntityContactEmail: '', // legal/leasing compoment for owner
                        ownerLegalEntityPersonRoleId: '', // legal/leasing compoment for owner

                        userType: legalPersonType.initialValue, // user type from enum legalPersonType
                        userIndividualName: '', // individual component for user
                        userIndividualLastName: '', // individual component for user
                        userIndividualUCN: '', // individual component for user
                        userIndividualCountry: constValues.countrySerbiaId, // individual component for user
                        userIndividualMyCountry: '', // individual component for user: textual representation of selected country
                        userIndividualCity: '', // individual component for user
                        userIndividualCityInSerbia: '', // individual component for user
                        userIndividualStreet: '', // individual component for user
                        userIndividualPhone: constValues.dialCodeSerbia, // individual component for user
                        userIndividualEmail: '', // individual component for user
                        userIndividualSubmitterRelationId: '', // individual component for user
                        userIndividualShouldShowBodyHarmForm: bodyHarm.InitialValue, // individual component for user
                        userIndividualReportingImmaterialDamage: null, // individual component for user
                        userIndividualPersonRoleId: '', // individual component for user
                        userIndividualImmaterialDamageDescription: '', // individual component for user

                        userLeasingEntityID: '', // legal/leasing compoment for user
                        userIndustrialClassificationType: '', // legal/leasing compoment for user
                        userLegalEntityCountry: constValues.countrySerbiaId, // legal/leasing compoment for user
                        userLegalEntityMyCountry: '', // legal/leasing compoment for user
                        userLegalEntityCity: '', // legal/leasing compoment for user
                        userLegalEntityCityInSerbia: '', // legal/leasing compoment for user
                        userLegalEntityStreet: '', // legal/leasing compoment for user
                        userLegalEntityPhone: constValues.dialCodeSerbia, // legal/leasing compoment for user
                        userLegalEntityContactPhone: constValues.dialCodeSerbia, // legal/leasing compoment for user
                        userLegalEntityName: '', // legal/leasing compoment for user
                        userLegalEntityID: '', // legal/leasing compoment for user
                        userLegalEntityVAT: '', // legal/leasing compoment for user
                        userLegalEntityEmail: '', // legal/leasing compoment for user
                        userLegalEntityContactName: '', // legal/leasing compoment for user
                        userLegalEntityContactLastName: '', // legal/leasing compoment for user
                        userLegalEntityContactEmail: '', // legal/leasing compoment for user
                        userLegalEntityPersonRoleId: '', // legal/leasing compoment for user

                        // there isn't driverType as driver can only be individual
                        driverType: legalPersonType.individual,
                        driverIndividualName: '', // individual component for driver
                        driverIndividualLastName: '', // individual component for driver
                        driverIndividualUCN: '', // individual component for driver
                        driverIndividualCountry: constValues.countrySerbiaId, // individual component for driver
                        driverIndividualMyCountry: '', // individual component for driver
                        driverIndividualCity: '', // individual component for driver
                        driverIndividualCityInSerbia: '', // individual component for driver
                        driverIndividualStreet: '', // individual component for driver
                        driverIndividualPhone: constValues.dialCodeSerbia, // individual component for driver
                        driverIndividualEmail: '', // individual component for driver
                        driverIndividualSubmitterRelationId: '', // individual component for driver
                        driverIndividualShouldShowBodyHarmForm: bodyHarm.InitialValue, // individual component for driver
                        driverIndividualReportingImmaterialDamage: null, // individual component for driver
                        driverIndividualPersonRoleId: constValues.driver, // individual component for driver
                        driverIndividualImmaterialDamageDescription: '', // individual component for driver
                        pickDriver: '', // driver section component

                        ownerBuildingType: legalPersonType.initialValue, // ownerBuilding type from enum legalPersonType
                        ownerBuildingIndividualName: '', // individual component for building owner
                        ownerBuildingIndividualLastName: '', // individual component for building owner
                        ownerBuildingIndividualUCN: '', // individual component for building owner
                        ownerBuildingIndividualCountry: constValues.countrySerbiaId, // individual component for building owner
                        ownerBuildingIndividualMyCountry: '', // individual component for building owner: textual representation of selected country
                        ownerBuildingIndividualCity: '', // individual component for building owner
                        ownerBuildingIndividualCityInSerbia: '', // individual component for building owner
                        ownerBuildingIndividualStreet: '', // individual component for building owner
                        ownerBuildingIndividualPhone: constValues.dialCodeSerbia, // individual component for building owner
                        ownerBuildingIndividualEmail: '', // individual component for building owner
                        ownerBuildingIndividualSubmitterRelationId: '', // individual component for building owner
                        ownerBuildingIndividualShouldShowBodyHarmForm: bodyHarm.InitialValue, // individual component for building owner
                        ownerBuildingIndividualReportingImmaterialDamage: null, // individual component for building owner
                        ownerBuildingIndividualPersonRoleId: '', // individual component for building owner
                        ownerBuildingIndividualImmaterialDamageDescription: '', // individual component for building owner

                        ownerBuildingLeasingEntityID: '', // legal/leasing compoment for building owner
                        ownerBuidingLeasingEntityID: '', // legal/leasing compoment for ownerBuiding
                        ownerBuidingIndustrialClassificationType: '', // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityCountry: constValues.countrySerbiaId, // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityMyCountry: '', // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityCity: '', // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityCityInSerbia: '', // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityStreet: '', // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityPhone: constValues.dialCodeSerbia, // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityContactPhone: constValues.dialCodeSerbia, // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityName: '', // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityID: '', // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityVAT: '', // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityEmail: '', // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityContactName: '', // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityContactLastName: '', // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityContactEmail: '', // legal/leasing compoment for ownerBuiding
                        ownerBuidingLegalEntityPersonRoleId: '', // legal/leasing compoment for ownerBuiding

                        // temporal data for editing injured person. data should be copied to indexed data on confirm (injured1IndividualName, etc...)
                        injuredIndividualName: '', // individual component for injured
                        injuredIndividualLastName: '', // individual component for injured
                        injuredIndividualUCN: '', // individual component for injured
                        injuredIndividualCountry: constValues.countrySerbiaId, // individual component for injured
                        injuredIndividualMyCountry: '', // individual component for injured
                        injuredIndividualCity: '', // individual component for injured
                        injuredIndividualCityInSerbia: '', // individual component for injured
                        injuredIndividualStreet: '', // individual component for injured
                        injuredIndividualPhone: constValues.dialCodeSerbia, // individual component for injured
                        injuredIndividualEmail: '', // individual component for injured
                        injuredIndividualSubmitterRelationId: '', // individual component for injured
                        injuredIndividualShouldShowBodyHarmForm: bodyHarm.InitialValue, // individual component for injured
                        injuredIndividualReportingImmaterialDamage: 'true', // individual component for injured
                        injuredIndividualPersonRoleId: '', // individual component for injured
                        injuredIndividualImmaterialDamageDescription: '', // individual component for building injured

                        isSubmitterOwnerOfVehicle: '', // damage vehicle section
                        vehicleLocation: '', // damage vehicle section
                        vehicleDamageDescription: '', // damage vehicle section
                        vehicleOtherDamages: '', // damage vehicle section
                        vehicleDamageMobile: '', // damage vehicle section

                        uploadDocList: [], // documentation
                        tempID: '',
                        mobileLabelStar: true,
                        oneTimePass: '',

                        // data from API
                        countries: [],
                        cities: [],
                        vehicleTypes: [],
                        leasingEntities: [],
                        submitterPersonRoles: [],
                        ownerPersonRoles: [],
                        userPersonRoles: [],
                        driverPersonRoles: [],
                        injuredPersonRoles: [],
                        ownerBuildingPersonRoles: [],
                        industrialClassifications: [],
                        submittedSuccessfully: false,
                        submitError: '',
                        skipSteps: [],
                    }}
                    onSubmit={(values, { setFieldValue, setSubmitting }) => {
                        submitTemporaryData(prepareDataForSubmit(values))
                            .then((response) => {
                                setFieldValue('tempID', response.data);
                                setFieldValue('submittedSuccessfully', true);
                                setFieldValue('submitError', '');
                                setSubmitting(false);
                            })
                            .catch((error) => {
                                setFieldValue('submittedSuccessfully', false);
                                setFieldValue('submitError', error.response?.data);
                                setSubmitting(false);
                            });
                    }}
                >
                    {({ values }) => (
                        <>
                            <MemoryRouter>
                                <IntroMessagesSlider
                                    open={values.introMessagesOpen}
                                    steps={introMessagesStepsComposer()}
                                    skipSteps={values.introMessagesSkipSteps}
                                />
                            </MemoryRouter>
                            <Form>
                                <MemoryRouter>
                                    <StyledCardSection>
                                        <StepsRouter steps={stepsComposer()} skipSteps={values.skipSteps} />
                                    </StyledCardSection>
                                </MemoryRouter>
                            </Form>
                        </>
                    )}
                </Formik>
            ) : (
                <CircularProgress />
            )}
        </>
    );
};

export default MtplApp;
